/**
 * Navigation Module
 *
 * Handles mobile menu toggle, keyboard navigation,
 * and focus trapping for accessibility.
 */

/**
 * Initialize navigation functionality.
 */
export function initNavigation() {
    const menuToggle = document.querySelector('.hf-menu-toggle');
    const mobileMenu = document.querySelector('.hf-mobile-menu');

    if (!menuToggle || !mobileMenu) {
        return;
    }

    const focusableElements = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    /**
     * Toggle mobile menu.
     */
    function toggleMenu() {
        const isOpen = mobileMenu.classList.contains('is-open');

        mobileMenu.classList.toggle('is-open');
        menuToggle.setAttribute('aria-expanded', String(!isOpen));
        document.body.classList.toggle('menu-open', !isOpen);

        if (!isOpen) {
            // Focus first element in menu
            const firstFocusable = mobileMenu.querySelector(focusableElements);
            firstFocusable?.focus();

            // Prevent body scroll
            document.body.style.overflow = 'hidden';
        } else {
            menuToggle.focus();
            document.body.style.overflow = '';
        }
    }

    /**
     * Close menu on Escape key.
     */
    function handleKeydown(event) {
        if (event.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
            toggleMenu();
        }
    }

    /**
     * Close menu when clicking outside.
     */
    function handleClickOutside(event) {
        if (
            mobileMenu.classList.contains('is-open') &&
            !mobileMenu.contains(event.target) &&
            !menuToggle.contains(event.target)
        ) {
            toggleMenu();
        }
    }

    // ── Event Listeners ─────────────────────────
    menuToggle.addEventListener('click', toggleMenu);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('click', handleClickOutside);

    // Close menu on resize to desktop
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    mediaQuery.addEventListener('change', (e) => {
        if (e.matches && mobileMenu.classList.contains('is-open')) {
            toggleMenu();
        }
    });
}

/**
 * Initialize an off-canvas wrapper for Ollie Menu Designer content.
 *
 * Expected markup:
 * - toggle buttons: .menu-toggle
 * - wrapper: .offcanvas-wrapper
 * - animated container: .container-canvas
 *
 * The overlay is created automatically if missing.
 */
export function initOllieOffCanvas({ toggleSelector = '.menu-toggle', wrapperSelector = '.offcanvas-wrapper', canvasSelector = '.container-canvas', overlaySelector = '.offcanvas-overlay', desktopMediaQuery = '(min-width: 992px)', } = {}) {
    const buttons = document.querySelectorAll(toggleSelector);

    if (buttons.length === 0) {
        return;
    }

    let overlay = document.querySelector(overlaySelector);
    let lastFocusedElement = null;
    let activeCanvas = null;
    let rootCanvas = null;

    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = overlaySelector.replace(/^\./, '');
        document.body.append(overlay);
    }

    const focusableElements = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    function getCanvasFromButton(button) {
        const controlsId = button.getAttribute('aria-controls');

        if (!controlsId) {
            return null;
        }

        const controlledElement = document.getElementById(controlsId);

        if (!controlledElement || !controlledElement.matches(canvasSelector)) {
            return null;
        }

        return controlledElement;
    }

    function setExpanded(button, isOpen) {
        button.setAttribute('aria-expanded', String(isOpen));
    }

    function closeCanvas() {
        if (!activeCanvas) {
            return;
        }

        const activeButton = document.querySelector(toggleSelector + '[aria-controls="' + activeCanvas.id + '"]');

        activeCanvas.classList.remove('is-open');
        //overlay.classList.remove('is-open');
        document.body.classList.remove('has-offcanvas-open');

        if (activeButton instanceof HTMLElement) {
            setExpanded(activeButton, false);
        }

        lastFocusedElement?.focus();
        activeCanvas = null;
        rootCanvas = null;
    }

    function openCanvas(button, canvas) {
        if (activeCanvas && activeCanvas !== canvas) {
            closeCanvas();
        }

        lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        activeCanvas = canvas;
        rootCanvas = canvas;

        canvas.classList.add('is-open');
        //overlay.classList.add('is-open');
        document.body.classList.add('has-offcanvas-open');
        setExpanded(button, true);

        const firstFocusable = canvas.querySelector(focusableElements);
        firstFocusable?.focus();
    }

    function showCanvas(canvas) {
        if (!(canvas instanceof HTMLElement)) {
            return;
        }

        if (activeCanvas && activeCanvas !== canvas) {
            activeCanvas.classList.remove('is-open');
        }

        activeCanvas = canvas;
        canvas.classList.add('is-open');

        const firstFocusable = canvas.querySelector(focusableElements);
        firstFocusable?.focus();
    }

    function handleBackButton(event) {
        event.preventDefault();
        closeCanvas();
    }

    function toggleCanvas(event) {
        const button = event.currentTarget;

        if (!(button instanceof HTMLElement)) {
            return;
        }

        const canvas = getCanvasFromButton(button);

        if (!canvas) {
            return;
        }

        event.preventDefault();

        if (canvas.classList.contains('is-open')) {
            closeCanvas();
            return;
        }

        openCanvas(button, canvas);
    }

    function handleKeydown(event) {
        if (event.key === 'Escape' && activeCanvas) {
            closeCanvas();
        }
    }

    function handleResize(event) {
        if (event.matches && activeCanvas) {
            closeCanvas();
        }
    }

    buttons.forEach((button) => {
        const canvas = getCanvasFromButton(button);

        if (!(button instanceof HTMLElement) || !canvas) {
            return;
        }

        setExpanded(button, false);
        button.addEventListener('click', toggleCanvas);
    });

    overlay.addEventListener('click', closeCanvas);
    document.addEventListener('keydown', handleKeydown);

    document.querySelectorAll(canvasSelector + ' .offcanvas-close').forEach((button) => {
        button.addEventListener('click', function (event) {
            event.preventDefault();
            closeCanvas();
        });
    });

    document.querySelectorAll(canvasSelector + ' .offcanvas-back').forEach((button) => {
        button.addEventListener('click', handleBackButton);
    });

    const mediaQuery = window.matchMedia(desktopMediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleResize);
    } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleResize);
    }
}

/**
 * Make the Linguise language switcher keyboard-accessible.
 *
 * The plugin renders the trigger as a plain <div> (no tabindex/role) that
 * only opens on :hover, so it's unreachable via Tab. This wires up focus,
 * click and keyboard (Enter/Space/Escape) support without touching the
 * plugin's own markup or hover behaviour.
 */
export function initLinguiseAccessibility() {
    document.querySelectorAll('.linguise_current_lang').forEach((trigger) => {
        const submenu = trigger.parentElement?.querySelector('.linguise_switcher_sub');

        if (!(trigger instanceof HTMLElement) || !submenu) {
            return;
        }

        trigger.setAttribute('tabindex', '0');
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');

        function open() {
            submenu.classList.add('is-keyboard-open');
            trigger.setAttribute('aria-expanded', 'true');
        }

        function close() {
            submenu.classList.remove('is-keyboard-open');
            trigger.setAttribute('aria-expanded', 'false');
        }

        function toggle() {
            if (submenu.classList.contains('is-keyboard-open')) {
                close();
            } else {
                open();
            }
        }

        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            toggle();
        });

        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggle();
            }

            if (event.key === 'Escape') {
                close();
                trigger.focus();
            }
        });

        document.addEventListener('focusout', (event) => {
            if (!trigger.parentElement.contains(event.relatedTarget)) {
                close();
            }
        });

        document.addEventListener('click', (event) => {
            if (!trigger.parentElement.contains(event.target)) {
                close();
            }
        });
    });
}

export function initCustomMobileMenuClose() {
    document.addEventListener('click', (event) => {
        const closeTrigger = event.target.closest('#close-mobile-menu');

        if (!closeTrigger) {
            return;
        }

        const nativeCloseButton = document.querySelector(
            '.wp-block-navigation__responsive-container-close'
        );

        if (nativeCloseButton) {
            nativeCloseButton.click();
        }
    });
}
